<template>
  <div ref="rootElement" :id="CLAIMS_WIDGET_ID" class="h-full w-full bg-background">
    <template v-if="!isConfigLoading">
      <main v-if="!configError" :class="`widget-${getClientTheme()}`">
        <h1 class="widget-heading-1" :class="componentStyles?.title">
          {{ mainHeading }}
        </h1>
        <Transition :name="transitionName" mode="out-in">
          <TheWelcome v-if="isWelcomeShown" :step-form="config.steps.welcomeStep!" @next="goToStepper" />
          <TheContextStep
            v-else-if="isTheContextStepShown"
            :value="config.context"
            :is-back-button-visible="!!config.steps.welcomeStep"
            :context="config.steps.contextStep!"
            @back="exitContextStep"
            @on-select="checkContactInfo"
            @on-de-select="setDefaultContactInfo"
            @on-finish="setContext"
          />
          <PolicySelector
            v-else-if="
              !selectedInsurance &&
              config.settings?.type === StepType.CLAIMS &&
              config.policy &&
              Array.isArray(config.policy) &&
              !!config.policy.length
            "
            @on-select="selectPolicy"
          />
          <Stepper v-else ref="stepper" @back="selectPolicy(-1)" />
        </Transition>
        <Transition>
          <ContactInfoCard
            v-if="!checkTheme('default') && isContactInfoShown && !isConfigLoading"
            v-bind="
              !!(config.steps.contactInfo?.phone && config.contact.phone) ||
              !!(config.steps.contactInfo?.mail && config.contact.email)
                ? config.steps.contactInfo
                : !!config.steps.contextStep
                  ? defaultContactConfig
                  : contactConfiguration
            "
            class="default-mt"
            :key="JSON.stringify(config.steps.contactInfo)"
          ></ContactInfoCard>
        </Transition>
      </main>
      <div
        class="w-full rounded-custom border-2 border-negative-500 bg-negative-100 p-4 font-bold text-negative-500"
        v-else
      >
        {{ configError }}
      </div>
    </template>
    <div v-else>
      <div role="status" class="animate-pulse">
        <div class="mb-4 h-4 rounded-custom bg-gray-200"></div>
        <div class="mb-4 h-4 rounded-custom bg-gray-200"></div>
        <div class="mb-4 h-[5.125rem] rounded-custom bg-gray-200"></div>
        <div class="mb-4 h-[5.125rem] rounded-custom bg-gray-200"></div>
        <div class="mb-4 h-[5.125rem] rounded-custom bg-gray-200"></div>
        <div class="mb-4 h-[5.125rem] rounded-custom bg-gray-200"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import PolicySelector from './components/PolicySelector.vue';
  import Stepper from './components/Stepper.vue';
  import TheWelcome from './components/TheWelcome.vue';
  import TheContextStep from './components/TheContextStep.vue';

  import { loadFonts } from '../../utils/fontUtils';
  import { loadAutocompleteStyle } from '../../utils/autocomplete';
  import { addGlobalStyles, applyTheme } from '../../utils/themeUtils';
  import { ref, watch, computed, onBeforeUnmount, nextTick, onMounted } from 'vue';
  import { CLAIMS_WIDGET_ID } from '../../constants';
  import { provide } from 'vue';
  import dayjs from 'dayjs';
  import customParseFormat from 'dayjs/plugin/customParseFormat';
  import utc from 'dayjs/plugin/utc';
  import { ContactInfoCardConfig, MailConfig, PhoneConfig, Policy, SelectorOptions, StepType } from 'src/types/widget';
  import { request } from 'src/utils/request';
  import { API_PATHS } from 'src/types/api';
  import { useSubmission } from '../form/util/composition';
  import { config, checkTheme, setWidgetInsuranceDetails, getClientTheme, getComponentStyle } from 'src/utils/config';
  import { sendAnalytics } from 'src/utils/analytics';
  import ContactInfoCard from './components/ContactInfoCard.vue';
  import { getLocationByIP } from 'src/utils/map';
  import { hasValidContactInfo } from 'src/utils/contactUtils';
  import { initializeApis } from 'src/composables/googleMaps';

  dayjs.extend(customParseFormat);
  dayjs.extend(utc);

  const componentStyles = computed(() => getComponentStyle());
  const rootElement = ref<HTMLDivElement>();
  const stepper = ref<InstanceType<typeof Stepper> | null>(null);
  const isContactInfoShown = computed(() => {
    const hasPhoneOrEmail = hasValidContactInfo({
      contact: {
        phone: config.value.contact?.phone,
        email: config.value.contact?.email,
      },
      settings: {
        contactInformation: config.value.settings?.contactInformation,
      },
    });
    if (config.value.steps.contactInfo === undefined && !hasPhoneOrEmail) return false;
    return (
      stepper.value?.selectedStep === 0 ||
      stepper.value?.selectedStep === 1 ||
      isWelcomeShown.value ||
      isTheContextStepShown.value ||
      config.value?.settings?.type === StepType.REFERRAL
    );
  });

  const defaultPhoneTitle = computed(() => {
    if (config.value?.settings?.type === StepType.REFERRAL) {
      return {
        title: 'Warm transferring to us?',
        description:
          'To warm transfer alongside submitting this referral, please call the Volvo Accident Assistance team using the corresponding phone number for the referral type',
      };
    }
    if (config.value?.settings?.type === StepType.ASSISTANCE) {
      return {
        title: 'Need immediate assistance?',
        description: `If you need immediate recovery, call us and we'll guide you through the best steps for your circumstances.`,
      };
    }
    return { title: 'Need to make a different claim?' };
  });

  provide(config.value.rootKey, rootElement as unknown as string);

  const isConfigLoading = computed(() => config.value.isConfigLoading);
  const configError = computed(() => config.value.error);
  const selectedInsurance = ref<Policy | undefined>(config.value.insurance || undefined);
  const transitionName = ref<'slide-left' | 'slide-right'>('slide-left');
  const isSubmitted = ref<boolean>(false);
  const { onSuccess, onChange } = useSubmission();

  onSuccess(() => {
    isSubmitted.value = true;
  });
  onChange(() => {
    isSubmitted.value = false;
  });

  const mainHeading = computed(() => {
    if (isSubmitted.value || config.value?.reference) {
      if (config.value.settings?.type === StepType.CLAIMS) return 'Your claim has been successfully submitted';
      return 'Details submitted successfully';
    }
    if (config.value?.settings?.mainTitle) {
      return config.value?.settings?.mainTitle;
    }
    if (config.value.settings?.type !== StepType.CLAIMS) {
      if (config.value.settings?.type === StepType.REFERRAL) {
        return 'Service Referral';
      }
      return 'Get support online';
    }
    return 'Make a claim online';
  });
  const isSelectorStarted = ref(false);
  const isWelcomeShown = computed(() => !isSelectorStarted.value && !!config.value.steps.welcomeStep);
  const isContextFinished = ref(false);
  const isTheContextStepShown = computed(() => !!config.value.steps.contextStep && !isContextFinished.value);

  function goToStepper() {
    isSelectorStarted.value = true;
    transitionName.value = 'slide-left';
  }

  const submissionReference = computed(() => config.value?.reference);
  const submission = computed(() => {
    return config.value?.sessionData?.submission;
  });
  const context = computed(() => config.value?.context || config.value?.sessionData?.submission?.context);
  const sessionStepKey = computed(() =>
    config.value?.sessionData &&
    config.value?.sessionData?.currentSession &&
    'stepKey' in config.value.sessionData.currentSession
      ? config.value?.sessionData?.currentSession?.stepKey || submission.value?.stepKey
      : submission.value?.stepKey || ''
  );

  const contactConfiguration = computed(() => {
    if (config.value?.steps?.contactInfo?.phone || config.value?.steps?.contactInfo?.mail) {
      const cloneConfig = { ...config.value?.steps?.contactInfo };
      if (config.value.settings?.contactInformation?.default) {
        config.value.settings?.contactInformation?.default.forEach((item) => {
          if ('phone' in item && item.phone && cloneConfig.phone) {
            cloneConfig.phone = {
              title: item?.description || '',
              ...cloneConfig.phone,
              number: item?.phone,
            };
          }
          if ('mail' in item && item?.mail && cloneConfig?.mail) {
            cloneConfig.mail = {
              title: item?.description || '',
              ...cloneConfig.mail,
              address: item?.mail,
            };
          }
          return;
        });
      }
      if (config.value.contact?.phone && cloneConfig.phone) {
        cloneConfig.phone = {
          ...cloneConfig.phone,
          number: config.value.contact?.phone,
        };
      }
      if (config.value.contact?.email && cloneConfig.mail) {
        cloneConfig.mail = {
          ...cloneConfig.mail,
          address: config.value.contact?.email,
        };
      }
      return cloneConfig;
    }

    if (
      config.value.settings?.contactInformation?.default &&
      (!config.value?.contact?.phone || !config.value?.contact?.email)
    ) {
      const cloneConfig = structuredClone(defaultPhoneTitle.value) as ContactInfoCardConfig;
      config.value.settings?.contactInformation?.default.forEach((item) => {
        if ('phone' in item && item.phone) {
          cloneConfig.phone = {
            title: item?.description || '',
            number: item?.phone,
          };
        }
        if ('mail' in item && item?.mail) {
          cloneConfig.mail = {
            title: item?.description || '',
            address: item?.mail,
          };
        }
        return;
      });
      return cloneConfig;
    }
    if (
      config.value.settings?.contactInformation?.default &&
      (config.value?.contact?.phone || config.value?.contact?.email)
    ) {
      const cloneConfig = structuredClone(defaultPhoneTitle.value) as ContactInfoCardConfig;
      config.value.settings?.contactInformation?.default.forEach((item) => {
        if ('phone' in item && config.value?.contact?.phone) {
          cloneConfig.phone = {
            title: item?.description || '',
            number: config.value?.contact?.phone,
          };
        }
        if ('mail' in item && config.value?.contact?.email) {
          cloneConfig.mail = {
            title: item?.description || '',
            address: config.value?.contact?.email,
          };
        }
        return;
      });
      return cloneConfig;
    }
    return defaultContactConfig.value;
  });

  async function selectPolicy(index: number) {
    let tempInsurance: Policy | undefined;
    if (index < 0) {
      transitionName.value = 'slide-right';
      if (config.value.steps.contextStep) {
        isContextFinished.value = false;
        setDefaultContactInfo();
      } else if (config.value.steps.welcomeStep) {
        isSelectorStarted.value = false;
      } else {
        tempInsurance = Array.isArray(config.value.policy) ? undefined : config.value.policy;
        selectedInsurance.value = undefined;
      }
    } else {
      transitionName.value = 'slide-left';
      tempInsurance = (config.value.policy as Policy[])[index];
    }
    if (tempInsurance) {
      setWidgetInsuranceDetails(tempInsurance);
    } else {
      config.value.insurance = undefined;
    }

    selectedInsurance.value = config.value.insurance;
    sendAnalytics('event', 'step_change', {
      event_category: 'STEP',
      event_label: 'POLICY_SELECTED',
      to: selectedInsurance.value,
      send_to: env.GA_ID,
    });
    if (!config.value.currentUserId || !config.value.sessionTracking) return;

    if (config.value.sessionData?.id) {
      await request.put(
        API_PATHS.session,
        config.value.sessionData?.id,
        JSON.parse(
          JSON.stringify(
            {
              missingProperties: {
                selectedInsurance: selectedInsurance.value,
                selectedUser: config.value.driver,
                vehicle: config.value.vehicle,
              },
              context: config.value.context,
            },
            (key, value) => (value === undefined ? '' : value)
          )
        )
      );
    }
  }

  watch(
    () => config.value.theme,
    () => {
      if (rootElement.value) {
        applyTheme(config.value.theme, rootElement.value);
        loadAutocompleteStyle(config.value.theme);
        loadFonts();
        addGlobalStyles();
      }
    }
  );
  watch(rootElement, () => {
    if (rootElement.value) {
      applyTheme(config.value.theme, rootElement.value);
      loadAutocompleteStyle(config.value.theme);
      loadFonts();
      addGlobalStyles();
    }
  });
  watch(isConfigLoading, () => {
    if (!isConfigLoading.value) {
      onWidgetLoad();
    }
    if (config.value?.reference) {
      selectedInsurance.value = !Array.isArray(config.value?.policy) ? config.value.policy : config.value.policy[0];
    }
  });

  watch(isTheContextStepShown, () => {
    if (config.value.steps?.contextStep && isTheContextStepShown.value) {
      if (!config.value.context) {
        config.value.context = {};
      }
      Object.entries(config.value.steps.contextStep).forEach(([key, context]) => {
        if (context.options.length === 1) {
          config.value.context![key] = context.options[0].value;
          if (config.value.steps.contextStep) {
            config.value.steps.contextStep[key].hidden = true;
          }
        }
      });
    }
  });
  async function onWidgetLoad() {
    selectedInsurance.value = config.value.insurance || undefined;
    if (config.value?.policy && (!Array.isArray(config.value?.policy) || config.value?.policy?.length === 1)) {
      selectedInsurance.value = !Array.isArray(config.value?.policy) ? config.value.policy : config.value.policy[0];
      config.value.insurance = selectedInsurance.value;
    }
    if (config.value.context && config.value.steps?.contextStep) {
      const acc: Record<string, string> = {};
      Object.entries(config.value.steps.contextStep).forEach(([key, ctx]) => {
        if (ctx.options.find((option) => option.value === config.value.context?.[key])) {
          acc[key] = config.value.context?.[key] || '';
        }
      });
      if (Object.keys(acc).length === Object.keys(config.value.steps.contextStep).length) {
        setContext(acc);
      }
    }
    sendAnalytics('set', 'session_id', String(config.value.currentUserId));
    sendAnalytics('set', 'user_id', String(config.value.currentUserId));
    await initializeApis();
    setTimeout(async () => {
      const [res] = await getLocationByIP();
      if ('body' in document && document.body) {
        const latLng = new google.maps.LatLng({
          lat: Number(res?.latitude) || 0,
          lng: Number(res?.longitude) || 0,
        });
        const mapElem = document.createElement('div');
        mapElem.id = 'test-map';
        document.body.appendChild(mapElem);
        const map = new google.maps.Map(mapElem, {
          zoom: 10,
          center: latLng,
          disableDefaultUI: true,
          disableDoubleClickZoom: true,
          mapId: 'test-map',
        });
        google.maps.event.addListenerOnce(map, 'idle', () => {
          mapElem.remove();
        });
      }
    }, 1000);

    if (sessionStepKey.value || submissionReference.value) {
      isSelectorStarted.value = true;
      isContextFinished.value = true;
    }
    if (
      submission.value &&
      submission.value?.context &&
      config.value?.steps?.contextStep &&
      'brand' in config.value.steps.contextStep &&
      'brand' in submission.value.context
    ) {
      const brandContext = config.value.steps.contextStep.brand;
      const brandValue = submission.value.context.brand;
      const selectedBrand = brandContext.options.find((option) => option.value === brandValue);
      if (selectedBrand) {
        checkContactInfo(selectedBrand);
      } else {
        setDefaultContactInfo();
      }
    } else {
      setDefaultContactInfo();
    }
  }

  onMounted(() => {
    if (sessionStepKey.value || submissionReference.value) {
      isSelectorStarted.value = true;
      isContextFinished.value = true;
    }
  });

  function setDefaultContactInfo(key?: string) {
    if (
      context?.value &&
      'brand' in context.value &&
      config.value.settings?.contactInformation &&
      context.value.brand.toLocaleLowerCase() in config.value.settings.contactInformation
    ) {
      checkContactInfo({ key: context.value.brand.toLowerCase() } as SelectorOptions);
      return;
    }
    if (key && key !== 'brand') return;
    const contactConfig = config.value.settings?.contactInformation;
    if (contactConfig && Object.keys(contactConfig)?.length > 1) {
      const [, selectedConfig] = Object.entries(contactConfig)
        .filter(([key]) => {
          return key !== 'default';
        })
        .reduce(
          ([accKey, accVal], [key, value]) => {
            if (value?.length > accVal.length) {
              return [key, value];
            }
            return [accKey, accVal];
          },
          ['', []]
        );

      const [mailArray, phoneArray] = separateContactInfo(selectedConfig);
      if (phoneArray?.length || mailArray.length) {
        nextTick(() => {
          defaultContactConfig.value = {
            ...(config.value.steps.contactInfo || defaultContactConfig.value),
            phone: phoneArray.map((item) => ({
              title: item.description,
              number: 'Please select the brand for the Client to see the relevant warm transfer lines',
            })),
            mail: mailArray.map((item) => ({
              title: item.description,
              address: 'Please select the brand for the Client to see the relevant warm transfer lines',
            })),
          };
        });
      }
    }
  }

  function separateContactInfo(contactConfig: Array<PhoneConfig | MailConfig>): [MailConfig[], PhoneConfig[]] {
    return contactConfig.reduce(
      ([mail, phone], item) => {
        return 'mail' in item ? [[...mail, item], phone] : [mail, [...phone, item]];
      },
      [[] as MailConfig[], [] as PhoneConfig[]]
    );
  }

  function preventScroll() {
    const scrollY = window.scrollY;
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--axi-widget-scroll-y', `${scrollY}px`);
    });
  }

  window.addEventListener('scroll', preventScroll, { passive: true });

  const defaultContactConfig = ref<ContactInfoCardConfig>({
    ...structuredClone(defaultPhoneTitle.value),
    phone: {
      title: 'Call us 24/7',
      number: config.value.contact.phone,
    },
    mail: {
      title: 'Send us an email',
      address: config.value.contact.email,
    },
  });

  function exitContextStep() {
    transitionName.value = 'slide-right';
    isSelectorStarted.value = false;
    config.value.context = {};
    setDefaultContactInfo();
  }

  function checkContactInfo(result: SelectorOptions) {
    if (
      'key' in result &&
      typeof result.key === 'string' &&
      config.value.settings?.contactInformation &&
      result.key in config.value.settings.contactInformation
    ) {
      const brandContactConfig =
        config.value.settings?.contactInformation?.[
          result.key as keyof typeof config.value.settings.contactInformation
        ];
      if (brandContactConfig) {
        const [mailArray, phoneArray] = separateContactInfo(brandContactConfig);
        nextTick(() => {
          defaultContactConfig.value = {
            ...(config.value.steps.contactInfo || defaultContactConfig.value),
            phone: phoneArray.map((item) => ({
              title: item.description,
              number: item.phone || item.number,
            })),
            mail: mailArray.map((item) => ({
              title: item.description,
              address: item.mail || item.address,
            })),
          };
        });
      }
    }
  }
  function setContext(result: Record<string, string>) {
    transitionName.value = 'slide-left';
    config.value.context = result;
    isSelectorStarted.value = true;
    isContextFinished.value = true;
  }

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', preventScroll);
  });
</script>

<style lang="sass">
  @use "../../assets/styles/main" as *
  @forward "../../assets/styles/main"
  @import '@uppy/core/dist/style.css'
  @import '@uppy/dashboard/dist/style.css'
  @import '@jsonforms/vue-vanilla/vanilla.css'
  @import 'vue-tel-input/vue-tel-input.css'
</style>
